import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, ApiError, post } from '../lib/api'
import type { CompanyHit, Filing, Holding, Page as FilingPage, Watchlist } from '../lib/types'
import { Button, Card, CategoryChip, CATEGORIES, ChangeText, EmptyState, ImpactBadge, LiveNumber, Skeleton, SymbolPicker, useToast } from '../ui'
import { useLiveStocks } from '../lib/liveMarket'
import { useConfig } from '../lib/config'
import { formatTimeIST } from '../lib/format'
import { Page } from './Page'
import styles from './p6.module.css'

interface Lists { data: Watchlist[]; limits: { lists: number; items: number }; prices_connected: boolean }
interface CalRow { id: string; symbol: string; name: string; action_type: string; description: string; event_date: string; held: boolean; watched: boolean }
type Tab = 'watchlist' | 'holdings' | 'events' | 'calendar'
const TABS: Array<[Tab, string]> = [['watchlist', 'Watchlist'], ['holdings', 'Holdings'], ['events', 'Events'], ['calendar', 'Calendar']]
const catLabel = (c: string | null) => (c && CATEGORIES[c]?.label) || (c ? c.replace(/_/g, ' ') : '')
const day = (d: string) => new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${String(d).slice(0, 10)}T00:00:00Z`))

function UpgradeNote({ children }: { children: string }) {
  return <div className={styles.upgrade} role="note">{children}<Link to="/pricing">See plans</Link></div>
}

export function WatchlistPage() {
  const live = useLiveStocks()
  const cfg = useConfig()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS.map(t => t[0]).find(t => t === params.get('tab')) ?? 'watchlist')
  const [lists, setLists] = useState<Lists | null>(null)
  const [holdings, setHoldings] = useState<Holding[] | null>(null)
  const [events, setEvents] = useState<Filing[] | null>(null)
  const [cal, setCal] = useState<CalRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    api<Lists>('/watchlists').then(setLists).catch(e => setError((e as Error).message))
    api<{ data: Holding[] }>('/holdings').then(r => setHoldings(r.data)).catch(() => setHoldings([]))
  }, [])
  useEffect(load, [load])

  const current = useMemo(() => {
    if (!lists?.data.length) return null
    const id = Number(params.get('list'))
    return lists.data.find(l => l.id === id) ?? lists.data[0]
  }, [lists, params])

  const mySymbols = useMemo(() => [...new Set([...(holdings ?? []).map(h => h.symbol), ...(lists?.data ?? []).flatMap(l => l.items.map(i => i.symbol))])], [holdings, lists])
  const heldSet = useMemo(() => new Set((holdings ?? []).map(h => h.symbol)), [holdings])

  useEffect(() => {
    if (!lists || !holdings) return
    if (!mySymbols.length) { setEvents([]); setCal([]); return }
    api<FilingPage<Filing>>(`/filings?date=today&limit=100&symbol=${encodeURIComponent(mySymbols.slice(0, 50).join(','))}`)
      .then(r => setEvents(r.data.filter(f => f.category !== 'routine'))).catch(() => setEvents([]))
    api<{ data: CalRow[] }>('/calendar?view=list').then(r => setCal(r.data.filter(a => a.held || a.watched).slice(0, 30))).catch(() => setCal([]))
  }, [lists, holdings, mySymbols])

  const heldToday = (events ?? []).filter(f => heldSet.has(f.symbol))
  const attention = heldToday.filter(f => (f.impact_score ?? 0) >= 70).length

  const setTab = (t: Tab) => { const n = new URLSearchParams(params); if (t === 'watchlist') n.delete('tab'); else n.set('tab', t); setParams(n, { replace: true }) }
  const selectList = (id: number) => { const n = new URLSearchParams(params); n.set('list', String(id)); n.delete('tab'); setParams(n, { replace: true }) }

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try { await fn(); if (ok) toast.show({ tone: 'success', message: ok }); load() }
    catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Something went wrong' }) }
  }
  const addSymbol = (h: CompanyHit) => current && run(() => post(`/watchlists/${current.id}/items`, { symbol: h.symbol }), `${h.symbol} added to ${current.name}`)
  const removeSymbol = (s: string) => current && run(() => api(`/watchlists/${current.id}/items/${encodeURIComponent(s)}`, { method: 'DELETE' }))
  const moveItem = (i: number, d: -1 | 1) => {
    if (!current) return
    const syms = current.items.map(x => x.symbol); const j = i + d
    if (j < 0 || j >= syms.length) return
    ;[syms[i], syms[j]] = [syms[j]!, syms[i]!]
    void run(() => api(`/watchlists/${current.id}/items/order`, { method: 'PUT', body: JSON.stringify({ symbols: syms }) }))
  }
  const moveList = (d: -1 | 1) => {
    if (!lists || !current) return
    const ids = lists.data.map(l => l.id); const i = ids.indexOf(current.id), j = i + d
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j]!, ids[i]!]
    void run(() => api('/watchlists/order', { method: 'PUT', body: JSON.stringify({ ids }) }))
  }
  const createList = () => {
    const name = newName.trim(); if (!name) return
    void run(async () => { const r = await post<{ data: Watchlist }>('/watchlists', { name }); setCreating(false); setNewName(''); selectList(r.data.id) }, `List “${name}” created`)
  }
  const renameList = () => {
    const name = newName.trim(); if (!name || !current) return
    void run(async () => { await api(`/watchlists/${current.id}`, { method: 'PATCH', body: JSON.stringify({ name }) }); setRenaming(false); setNewName('') }, 'List renamed')
  }
  const deleteList = () => {
    if (!current || !window.confirm(`Delete the list “${current.name}”? The stocks stay in your other lists.`)) return
    void run(async () => { await api(`/watchlists/${current.id}`, { method: 'DELETE' }); const n = new URLSearchParams(params); n.delete('list'); setParams(n, { replace: true }) }, 'List deleted')
  }

  const atListLimit = !!lists && lists.data.length >= lists.limits.lists
  const atItemLimit = !!lists && !!current && current.items.length >= lists.limits.items

  return (
    <Page title="Watchlist" subtitle="Your lists with today's filings, holdings, events and upcoming corporate actions">
      {holdings && holdings.length > 0 && events && (
        <div className={styles.banner} role="status">
          <span>{heldToday.length} filing{heldToday.length === 1 ? '' : 's'} on your holdings today{attention ? ` · ${attention} need${attention === 1 ? 's' : ''} attention` : ''}</span>
          <Link to="/announcements?mine=1&date=today">View</Link>
        </div>
      )}
      <div role="group" aria-label="Show" className={styles.seg}>
        {TABS.map(([t, l]) => <button key={t} type="button" aria-pressed={tab === t} className={tab === t ? styles.segOn : styles.segBtn} onClick={() => setTab(t)}>{l}</button>)}
      </div>

      {error && <Card variant="flush"><EmptyState kind="error" title="Could not load your lists" compact>{error}</EmptyState></Card>}
      {!error && !lists && <Card><Skeleton lines={5} label="Loading watchlists" /></Card>}

      {lists && tab === 'watchlist' && (
        <Card variant="flush" title={current ? current.name : 'Watchlists'}
          action={<span className={styles.muted}>{current ? `${current.items.length} of ${lists.limits.items} stocks` : ''}{lists.prices_connected ? '' : ' · prices: not connected'}</span>}>
          <div className={styles.pad} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lists.data.length > 0 && (
              <div className={styles.between}>
                <div role="group" aria-label="Your lists" className={styles.seg}>
                  {lists.data.map(l => <button key={l.id} type="button" aria-pressed={current?.id === l.id} className={current?.id === l.id ? styles.segOn : styles.segBtn} onClick={() => selectList(l.id)}>{l.name} <span className={styles.muted}>{l.items.length}</span></button>)}
                </div>
                {current && (
                  <div className={styles.row}>
                    <button type="button" className={`${styles.iconBtn} ${styles.reorder}`} aria-label="Move list left" disabled={lists.data[0]?.id === current.id} onClick={() => moveList(-1)}>←</button>
                    <button type="button" className={`${styles.iconBtn} ${styles.reorder}`} aria-label="Move list right" disabled={lists.data[lists.data.length - 1]?.id === current.id} onClick={() => moveList(1)}>→</button>
                    <Button size="sm" onClick={() => { setRenaming(true); setCreating(false); setNewName(current.name) }}>Rename</Button>
                    <Button size="sm" variant="ghost" onClick={deleteList}>Delete list</Button>
                  </div>
                )}
              </div>
            )}
            {(renaming || creating) ? (
              <form className={styles.row} onSubmit={e => { e.preventDefault(); if (creating) createList(); else renameList() }}>
                <label className={styles.field}><span>{creating ? 'New list name' : 'Rename list'}</span>
                  <input className={styles.input} maxLength={60} value={newName} onChange={e => setNewName(e.target.value)} autoFocus required /></label>
                <div className={styles.row} style={{ alignSelf: 'flex-end' }}><Button type="submit" variant="primary">{creating ? 'Create' : 'Save'}</Button><Button onClick={() => { setRenaming(false); setCreating(false) }}>Cancel</Button></div>
              </form>
            ) : atListLimit ? (
              lists.limits.lists < 5 ? <UpgradeNote>{`Your plan includes ${lists.limits.lists} watchlist. Pro and Trader include up to 5.`}</UpgradeNote> : <p className={styles.muted}>You have the maximum of 5 lists.</p>
            ) : <div><Button size="sm" onClick={() => { setCreating(true); setRenaming(false); setNewName('') }}>+ New list</Button></div>}
            {current && (atItemLimit
              ? <UpgradeNote>{`This list is full (${lists.limits.items} stocks on your plan).`}</UpgradeNote>
              : <SymbolPicker label={`Add a stock to ${current.name}`} onSelect={addSymbol} />)}
          </div>
          {!current ? <EmptyState title="No watchlists yet" action={<Button variant="primary" onClick={() => setCreating(true)}>Create a list</Button>}>Create a list and add stocks to see their filings as they arrive.</EmptyState>
            : current.items.length === 0 ? <EmptyState title="This list is empty" compact>Search above to add stocks.</EmptyState>
            : (
              <ul className={styles.list} aria-label={`Stocks in ${current.name}`}>
                {current.items.map((i, idx) => (
                  <li key={i.symbol} className={styles.item}>
                    <div className={styles.grow}>
                      <div className={styles.row}>
                        <Link to={`/stock/${encodeURIComponent(i.symbol)}`} className={styles.sym}>{i.symbol}</Link>
                        {heldSet.has(i.symbol) && <span className={styles.pill}>Holding</span>}
                        {i.filing_category && <Link to={`/stock/${encodeURIComponent(i.symbol)}?tab=filings`} className={styles.badge} title={i.filing_headline ?? ''}>{catLabel(i.filing_category)} · today</Link>}
                      </div>
                      <span className={styles.name}>{i.name}</span>
                    </div>
                    {cfg.prices_enabled
                      ? <span className={styles.px}><LiveNumber value={live(i.symbol)?.ltp ?? i.ltp ?? null} /><ChangeText percent={live(i.symbol)?.change_pct ?? i.change_pct ?? null} size="sm" /></span>
                      : <span className={styles.exch}>NSE</span>}
                    <div className={styles.row} style={{ flexWrap: 'nowrap' }}>
                      <button type="button" className={`${styles.iconBtn} ${styles.reorder}`} aria-label={`Move ${i.symbol} up`} disabled={idx === 0} onClick={() => moveItem(idx, -1)}>↑</button>
                      <button type="button" className={`${styles.iconBtn} ${styles.reorder}`} aria-label={`Move ${i.symbol} down`} disabled={idx === current.items.length - 1} onClick={() => moveItem(idx, 1)}>↓</button>
                      <button type="button" className={styles.iconBtn} aria-label={`Remove ${i.symbol}`} onClick={() => removeSymbol(i.symbol)}>✕</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      )}

      {lists && tab === 'holdings' && (
        <Card variant="flush" title="Holdings" action={<Link to="/portfolio">Manage</Link>}>
          {!holdings ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading holdings" /></div>
            : holdings.length === 0 ? <EmptyState title="No holdings yet" action={<Link to="/portfolio">Import holdings</Link>}>Import your holdings to follow their filings.</EmptyState>
            : (
              <ul className={styles.list} aria-label="Your holdings">
                {holdings.map(h => {
                  const today = (events ?? []).find(f => f.symbol === h.symbol)
                  return (
                    <li key={h.symbol} className={styles.item}>
                      <div className={styles.grow}>
                        <div className={styles.row}>
                          <Link to={`/stock/${encodeURIComponent(h.symbol)}`} className={styles.sym}>{h.symbol}</Link>
                          {today && <span className={styles.badge}>{catLabel(today.category)}{today.impact_score != null ? ` · ${today.impact_score}` : ''}</span>}
                          {h.risks_90d > 0 && <span className={`${styles.pill} ${styles.pillDown}`}>Risk</span>}
                        </div>
                        <span className={styles.name}>{h.name} · {h.quantity.toLocaleString('en-IN')} shares</span>
                      </div>
                      <span className={styles.exch}>NSE</span>
                    </li>
                  )
                })}
              </ul>
            )}
        </Card>
      )}

      {lists && tab === 'events' && (
        <Card variant="flush" title="Today's filings for your stocks" action={<Link to="/announcements?date=today&mine=1">Holdings in Announcements</Link>}>
          {!events ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading filings" /></div>
            : events.length === 0 ? <EmptyState title="No filings today" compact>{mySymbols.length ? 'None of your stocks has filed anything notable today.' : 'Add stocks to a watchlist or import holdings.'}</EmptyState>
            : (
              <ul className={styles.list} aria-label="Today's filings">
                {events.map(f => (
                  <li key={f.id} className={styles.item}>
                    <ImpactBadge score={f.impact_score} size="sm" />
                    <div className={styles.grow}>
                      <div className={styles.row}><Link to={`/stock/${encodeURIComponent(f.symbol)}?tab=filings`} className={styles.sym}>{f.symbol}</Link><CategoryChip category={f.category} size="sm" />{heldSet.has(f.symbol) && <span className={styles.pill}>Holding</span>}<span className={styles.muted} style={{ marginLeft: 'auto' }}>{formatTimeIST(f.filed_at)}</span></div>
                      <span className={styles.riskDetail}>{f.headline}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      )}

      {lists && tab === 'calendar' && (
        <Card variant="flush" title="Upcoming actions for your stocks" action={<Link to="/calendar">Full calendar</Link>}>
          {!cal ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading actions" /></div>
            : cal.length === 0 ? <EmptyState title="Nothing scheduled" compact>No ex-dates or board meetings in the next 60 days for your stocks.</EmptyState>
            : (
              <ul className={styles.list} aria-label="Upcoming actions">
                {cal.map(a => (
                  <li key={a.id} className={styles.item}>
                    <span className={styles.num} style={{ minWidth: 56, textAlign: 'left' }}>{day(a.event_date)}</span>
                    <div className={styles.grow}>
                      <div className={styles.row}><Link to={`/stock/${encodeURIComponent(a.symbol)}`} className={styles.sym}>{a.symbol}</Link>{a.held && <span className={styles.pill}>Holding</span>}</div>
                      <span className={styles.riskDetail}>{a.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      )}
    </Page>
  )
}
